import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import StockChart from './StockChart';
import { useGridCopyMenu } from './gridCommon';
import { getMainAxios } from '../utils/util';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

// 테마 목록 마스터
interface ThemeMaster {
  themeid: number;
  themenm: string;
  themestartd: string;       // 테마시작일자
  prodcnt: number;           // 테마 종목수
  firstcollectd: string;
  lastcollectd: string;
  remark: string | null;     // 비고 (최신 변경)
  prodcntupdt: string | null;// 종목수 변경시간
  nmupdt: string | null;     // 이름 변경시간
  diffrt: number | null;     // 전일대비 등락률
  avg3rt: number | null;     // 3일평균 등락률
}
interface MasterResp { items: ThemeMaster[]; }

interface Pdf {
  prodid: string; prodnm: string;
  naver_c: number | null; naver_diff: number | null; naver_diffrt: number | null;
  naver_ta: number | null; naver_v: number | null;
  kw_clsprc: number | null; kw_flurt: number | null;
  kw_qty: number | null; kw_amt: number | null; kw_d: string | null;
  mktcap: number | null; wei: number | null;  // 시가총액 / 구성비중(시총기준 %)
}
interface PdfResp { d: string; themeid: number; items: Pdf[]; }

interface ChartRow { d: string; openprc: number; highprc: number; lowprc: number; clsprc: number; flurt: number | null; qty: number; }

const fmtNum = (n: any) => n == null ? '' : Number(n).toLocaleString();
const fmtPct = (n: any) => n == null ? '' : Number(n).toFixed(2);
// 등락률 색상: 상승 빨강 / 하락 파랑 / 보합 기본
const rtColor = (v: any) => (v ?? 0) > 0 ? '#dc2626' : (v ?? 0) < 0 ? '#2563eb' : undefined;
// 등락률 셀 스타일 (우측정렬 + 색상)
const rtCell = (p: any) => ({ textAlign: 'right' as const, fontFamily: 'monospace', color: rtColor(p.value) });
// YY.MM.DD (예: 26.05.28)
const fmtDate = (s: any) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getFullYear()).slice(-2)}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};
// YY.MM.DD HH:mm
const fmtDateTime = (s: any) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return `${fmtDate(s)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const NaverTheme: React.FC = () => {
  const axios = getMainAxios();
  const copyMenu = useGridCopyMenu();
  const [masters, setMasters] = useState<ThemeMaster[]>([]);
  const [selectedTheme, setSelectedTheme] = useState<ThemeMaster | null>(null);
  const [pdfs, setPdfs] = useState<Pdf[]>([]);
  const [selectedProd, setSelectedProd] = useState<Pdf | null>(null);
  const [chartRows, setChartRows] = useState<ChartRow[]>([]);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  // 차트/일별시세 기간 — 종료일은 데이터 최신일자, 시작일은 그로부터 1개월 전
  const isoDay = (dt: Date) => dt.toISOString().slice(0, 10);
  const monthBefore = (iso: string) => {
    const d = new Date(iso + 'T00:00:00'); d.setMonth(d.getMonth() - 1); return isoDay(d);
  };
  // 데이터 최신일자 (master.lastcollectd 최댓값). 미로딩 시 오늘.
  const [dataMaxDate, setDataMaxDate] = useState<string>(() => isoDay(new Date()));
  // 상단 기준일자 — 구성종목/시세 조회 기준
  const [baseDate, setBaseDate] = useState<string>(() => isoDay(new Date()));
  const [fromDate, setFromDate] = useState<string>(() => monthBefore(isoDay(new Date())));
  const [toDate, setToDate] = useState<string>(() => isoDay(new Date()));

  const fetchMasters = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get<MasterResp>('/api/v1/navertheme/master');
      const items = r.data.items ?? [];
      setMasters(items);
      // 데이터 최신일자로 기간 기본값 보정 (lastcollectd 최댓값)
      const maxIso = items.reduce<string>((mx, it) => {
        const v = it.lastcollectd ? it.lastcollectd.slice(0, 10) : '';
        return v > mx ? v : mx;
      }, '');
      if (maxIso) {
        setDataMaxDate(maxIso);
        setBaseDate(maxIso);
        setToDate(maxIso);
        setFromDate(monthBefore(maxIso));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [axios]);

  const fetchChart = useCallback(async (p: Pdf, from?: string, to?: string) => {
    setSelectedProd(p);
    try {
      const r = await axios.get<ChartRow[]>('/api/v1/navertheme/chart', {
        params: { prodid: p.prodid, from: from ?? fromDate, to: to ?? toDate },
      });
      setChartRows(r.data ?? []);
    } catch (e) { console.error(e); setChartRows([]); }
  }, [axios, fromDate, toDate]);

  const fetchPdfs = useCallback(async (t: ThemeMaster, d?: string) => {
    const rid = ++reqIdRef.current;
    setSelectedTheme(t);
    setPdfs([]);
    setSelectedProd(null);
    setChartRows([]);
    try {
      const r = await axios.get<PdfResp>('/api/v1/navertheme/pdf', {
        params: { themeid: t.themeid, d: d ?? baseDate },
      });
      if (rid !== reqIdRef.current) return;
      setPdfs(r.data.items ?? []);
      // 첫 종목 자동 선택
      if (r.data.items?.length > 0) fetchChart(r.data.items[0]);
    } catch (e) { console.error(e); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axios, baseDate]);

  // [조회] 버튼 — 현재 선택 종목 일별시세를 입력 기간으로 재조회
  const applyPeriod = useCallback(() => {
    if (selectedProd) fetchChart(selectedProd, fromDate, toDate);
  }, [selectedProd, fromDate, toDate, fetchChart]);

  // 기간(보정 포함) 변경 시 선택 종목 일별시세 자동 재조회
  useEffect(() => {
    if (selectedProd) fetchChart(selectedProd, fromDate, toDate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  useEffect(() => { fetchMasters(); }, [fetchMasters]);

  useEffect(() => {
    if (masters.length > 0 && !selectedTheme) fetchPdfs(masters[0]);
  }, [masters, selectedTheme, fetchPdfs]);

  // 기준일자 변경 시 선택 테마 구성종목을 해당 일자로 재조회
  useEffect(() => {
    if (selectedTheme) fetchPdfs(selectedTheme, baseDate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseDate]);

  const themeCols = useMemo<ColDef<ThemeMaster>[]>(() => [
    { headerName: 'ID', field: 'themeid', width: 60, cellStyle: { textAlign: 'right' } },
    { headerName: '테마명', field: 'themenm', flex: 1, minWidth: 150 },
    { headerName: '전일대비', field: 'diffrt', width: 80,
      valueFormatter: p => p.value != null ? fmtPct(p.value) : '',
      cellStyle: rtCell },
    { headerName: '3일평균', field: 'avg3rt', width: 80,
      valueFormatter: p => p.value != null ? fmtPct(p.value) : '',
      cellStyle: rtCell },
    { headerName: '종목수', field: 'prodcnt', width: 70,
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '테마시작일자', field: 'themestartd', width: 100,
      valueFormatter: p => fmtDate(p.value),
      cellStyle: { textAlign: 'center', fontSize: '11px' } },
    { headerName: '비고', field: 'remark', flex: 1, minWidth: 140,
      valueFormatter: p => p.value ?? '',
      cellStyle: { fontSize: '11px', color: '#b45309' } },
    { headerName: '종목수변경', field: 'prodcntupdt', width: 110,
      valueFormatter: p => fmtDateTime(p.value),
      cellStyle: { textAlign: 'center', fontSize: '11px', color: '#6b7280' } },
    { headerName: '이름변경', field: 'nmupdt', width: 110,
      valueFormatter: p => fmtDateTime(p.value),
      cellStyle: { textAlign: 'center', fontSize: '11px', color: '#6b7280' } },
  ], []);

  const pdfCols = useMemo<ColDef<Pdf>[]>(() => [
    { headerName: '코드', field: 'prodid', width: 80 },
    { headerName: '종목명', field: 'prodnm', flex: 1, minWidth: 130 },
    { headerName: '구성비중', field: 'wei', width: 80,
      valueFormatter: p => p.value != null ? fmtPct(p.value) : '',
      cellStyle: { textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: '#1e40af' } },
    { headerName: '현재가', field: 'naver_c', width: 90,
      valueFormatter: p => fmtNum(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '등락률', field: 'naver_diffrt', width: 80,
      valueFormatter: p => p.value != null ? fmtPct(p.value) : '',
      cellStyle: rtCell },
    { headerName: 'kw종가', field: 'kw_clsprc', width: 90,
      valueFormatter: p => fmtNum(p.value),
      cellStyle: { textAlign: 'right', fontFamily: 'monospace', color: '#6b7280' } },
    { headerName: 'kw등락%', field: 'kw_flurt', width: 80,
      valueFormatter: p => p.value != null ? fmtPct(p.value) : '',
      cellStyle: rtCell },
    { headerName: 'kw일자', field: 'kw_d', width: 100,
      valueFormatter: p => p.value ? p.value.slice(0, 10) : '',
      cellStyle: { textAlign: 'center', color: '#6b7280', fontSize: '11px' } },
  ], []);

  // 일별 시세 그리드 (선택 종목, 기간 내)
  const dailyCols = useMemo<ColDef<ChartRow>[]>(() => [
    { headerName: '일자', field: 'd', width: 100,
      valueFormatter: p => fmtDate(p.value),
      cellStyle: { textAlign: 'center', fontSize: '11px' } },
    { headerName: '시가', field: 'openprc', width: 90,
      valueFormatter: p => fmtNum(p.value), cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '고가', field: 'highprc', width: 90,
      valueFormatter: p => fmtNum(p.value), cellStyle: { textAlign: 'right', fontFamily: 'monospace', color: '#991b1b' } },
    { headerName: '저가', field: 'lowprc', width: 90,
      valueFormatter: p => fmtNum(p.value), cellStyle: { textAlign: 'right', fontFamily: 'monospace', color: '#1e40af' } },
    { headerName: '종가', field: 'clsprc', width: 90,
      valueFormatter: p => fmtNum(p.value), cellStyle: { textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 } },
    { headerName: '등락률', field: 'flurt', width: 80,
      valueFormatter: p => p.value != null ? fmtPct(p.value) : '',
      cellStyle: rtCell },
    { headerName: '거래량', field: 'qty', flex: 1, minWidth: 110,
      valueFormatter: p => fmtNum(p.value), cellStyle: { textAlign: 'right', fontFamily: 'monospace', color: '#6b7280' } },
  ], []);

  // 일별 시세는 최신일자 먼저
  const dailyRows = useMemo(() => [...chartRows].reverse(), [chartRows]);

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>

      {/* 헤더 */}
      <div style={{
        backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
        padding: '16px', marginBottom: '20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: '#1e40af', fontSize: '14px', fontWeight: 600 }}>
            네이버 테마별 시세
          </h3>
          <button onClick={fetchMasters} disabled={loading} style={{
            padding: '4px 12px', background: '#3b82f6', color: 'white',
            border: 'none', borderRadius: '4px', fontSize: '12px',
            cursor: loading ? 'wait' : 'pointer',
          }}>
            {loading ? '⏳' : '🔄'} 새로고침
          </button>

          {/* 기준일자 달력 — 해당 일자 기준 구성종목/시세 조회 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>기준일자</span>
            <input type="date" value={baseDate} max={dataMaxDate}
              onChange={e => setBaseDate(e.target.value)}
              style={{ fontSize: '12px', padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: '4px' }} />
            <button onClick={() => setBaseDate(dataMaxDate)} style={{
              padding: '3px 8px', background: '#dbeafe', color: '#1e40af',
              border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer',
            }}>최신</button>
          </div>

          <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#6b7280' }}>
            데이터 최신일 {fmtDate(dataMaxDate)} · 테마 {masters.length}개
          </span>
        </div>
      </div>

      {/* 메인 — 좌(테마 마스터) · 우(구성종목 + 일별시세 + 차트) */}
      <div style={{ display: 'grid', gridTemplateColumns: '760px 1fr', gap: '20px' }}>

        {/* 좌: 테마 목록 마스터 */}
        <div style={{
          backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
          padding: '12px',
        }}>
          <div style={{ marginBottom: '8px', fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>
            테마 목록 마스터 (종목수 내림차순) · 등락률 <span style={{ color: '#dc2626' }}>상승</span>/<span style={{ color: '#2563eb' }}>하락</span> · 변경 시 비고/변경시간 표시
          </div>
          <div className="ag-theme-quartz" style={{ height: 740, width: '100%', fontSize: '12px' }}>
            <AgGridReact<ThemeMaster>
              rowData={masters}
              columnDefs={themeCols}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              onRowClicked={e => e.data && fetchPdfs(e.data)}
              getRowStyle={p => p.data?.themeid === selectedTheme?.themeid ? { backgroundColor: '#dbeafe' } : undefined}
              rowHeight={28}
              headerHeight={30}
              {...copyMenu.gridProps}
            />
          </div>
        </div>

        {/* 우: 구성종목 + 차트 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* 우-상: 구성종목 */}
          <div style={{
            backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
            padding: '12px',
          }}>
            <div style={{ marginBottom: '8px', fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>
              구성 종목
              {selectedTheme && (
                <span style={{ marginLeft: 8, fontWeight: 400, color: '#6b7280' }}>
                  — {selectedTheme.themenm} ({pdfs.length}개) · 행 클릭 시 차트
                </span>
              )}
            </div>
            <div className="ag-theme-quartz" style={{ height: 340, width: '100%', fontSize: '12px' }}>
              <AgGridReact<Pdf>
                rowData={pdfs}
                columnDefs={pdfCols}
                defaultColDef={{ sortable: true, filter: true, resizable: true }}
                onRowClicked={e => e.data && fetchChart(e.data)}
                getRowStyle={p => p.data?.prodid === selectedProd?.prodid ? { backgroundColor: '#dbeafe' } : undefined}
                rowHeight={26}
                headerHeight={30}
                {...copyMenu.gridProps}
              />
            </div>
          </div>

          {/* 우-중: 일별 시세 + 기간 선택 */}
          <div style={{
            backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
            padding: '12px',
          }}>
            <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>
                일별 시세
                {selectedProd && (
                  <span style={{ marginLeft: 8, fontWeight: 400, color: '#6b7280' }}>
                    — {selectedProd.prodnm} ({selectedProd.prodid})
                  </span>
                )}
                <span style={{ marginLeft: 8, fontWeight: 400, color: '#9ca3af', fontSize: '11px' }}>
                  데이터 최신일 {fmtDate(dataMaxDate)}
                </span>
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input type="date" value={fromDate} max={toDate}
                  onChange={e => setFromDate(e.target.value)}
                  style={{ fontSize: '11px', padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: '4px' }} />
                <span style={{ fontSize: '11px', color: '#6b7280' }}>~</span>
                <input type="date" value={toDate} min={fromDate} max={dataMaxDate}
                  onChange={e => setToDate(e.target.value)}
                  style={{ fontSize: '11px', padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: '4px' }} />
                <button onClick={applyPeriod} disabled={!selectedProd} style={{
                  padding: '3px 10px', background: '#3b82f6', color: 'white',
                  border: 'none', borderRadius: '4px', fontSize: '11px',
                  cursor: selectedProd ? 'pointer' : 'not-allowed',
                }}>조회</button>
              </span>
            </div>
            <div className="ag-theme-quartz" style={{ height: 240, width: '100%', fontSize: '12px' }}>
              <AgGridReact<ChartRow>
                rowData={dailyRows}
                columnDefs={dailyCols}
                defaultColDef={{ sortable: true, filter: true, resizable: true }}
                rowHeight={24}
                headerHeight={30}
                {...copyMenu.gridProps}
              />
            </div>
          </div>

          {/* 우-하: 차트 (공통 StockChart — 캔들 + 거래량, 일/주/월 토글 내장) */}
          <div style={{
            backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
            padding: '12px',
          }}>
            <div style={{ marginBottom: '8px', fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>
              일봉 차트
              {selectedProd && (
                <span style={{ marginLeft: 8, fontWeight: 400, color: '#6b7280' }}>
                  — {selectedProd.prodnm} ({selectedProd.prodid})
                </span>
              )}
            </div>
            {selectedProd ? (
              <StockChart prodId={selectedProd.prodid} prodNm={selectedProd.prodnm} height={380} />
            ) : (
              <div style={{ padding: '60px 0', textAlign: 'center', color: '#6b7280', fontSize: '12px' }}>
                좌측에서 테마 선택 → 우측에서 종목 선택 시 차트 표시
              </div>
            )}
          </div>
        </div>
      </div>

      {copyMenu.menu}
    </div>
  );
};

export default NaverTheme;
